/**
 * Tema MUI a partir dos tokens do design system publicado da Sobral Nunes
 * (artifact a753ef8a-b85b-4b6b-ad66-b58c724d6d6a, project/tokens.json).
 *
 * Regras de uso (docs/contexto/06): navy para estrutura e títulos; ciano para destaque,
 * foco e ações, nunca em bloco de texto; gelo como fundo; Quicksand como fonte única.
 */
import { defaultTheme, type RaThemeOptions } from "react-admin";

export const tokens = {
  navy: "#012639",
  ciano: "#00b0b3",
  gelo: "#f2f4f5",
  tinta: "#0d2733",
  valor: "#33474f",
  apagado: "#8a979d",
  navySuave: "#8fb3bd",
  branco: "#ffffff",
  fonte: '"Quicksand", "Segoe UI", system-ui, sans-serif',
} as const;

/** Fundo e cabeçalho das telas de acesso (classes RaLogin-* e RaAuthLayout-*). */
const telaDeAcesso = {
  backgroundImage:
    "radial-gradient(1200px 700px at 80% -8%, rgba(0,176,179,.12), transparent 60%), linear-gradient(160deg, #ffffff 0%, #F2F4F5 58%, #e7ecee 100%)",
  "& .RaLogin-card, & .RaAuthLayout-card": {
    minWidth: 300,
    boxShadow: "0 1px 2px rgba(1, 38, 57, 0.08), 0 0 0 1px rgba(1, 38, 57, 0.06)",
  },
  "& .RaLogin-icon, & .RaAuthLayout-icon": {
    width: 72,
    height: 72,
    backgroundColor: "transparent",
    backgroundImage: "url(/logo-icone-192.png)",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    "& svg": { display: "none" },
  },
};

const temaSobral: RaThemeOptions = {
  palette: {
    mode: "light",
    primary: { main: tokens.navy, contrastText: tokens.branco },
    secondary: { main: tokens.ciano, contrastText: tokens.branco },
    background: { default: tokens.gelo, paper: tokens.branco },
    text: { primary: tokens.tinta, secondary: tokens.valor, disabled: tokens.apagado },
    divider: "rgba(1, 38, 57, 0.12)",
  },
  typography: {
    fontFamily: tokens.fonte,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: { fontWeight: 700, color: tokens.navy },
    h2: { fontWeight: 700, color: tokens.navy },
    h3: { fontWeight: 700, color: tokens.navy },
    h4: { fontWeight: 700, color: tokens.navy },
    h5: { fontWeight: 700, color: tokens.navy },
    h6: { fontWeight: 700, color: tokens.navy },
    button: { fontWeight: 700, textTransform: "none" },
  },
  shape: { borderRadius: 8 },
  sidebar: { width: 240, closedWidth: 56 },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: tokens.navy, color: tokens.branco, boxShadow: "none" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        elevation1: { boxShadow: "0 1px 2px rgba(1, 38, 57, 0.08), 0 0 0 1px rgba(1, 38, 57, 0.06)" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tokens.ciano },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: { "&::after": { borderBottomColor: tokens.ciano } },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { "&.Mui-focused": { color: tokens.navy } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 700, color: tokens.navy },
      },
    },
    // Telas de login, convite e recuperação de senha: fundo do design system (token sn-bg) e o ícone da marca.
    RaLogin: { styleOverrides: { root: telaDeAcesso } },
    RaAuthLayout: { styleOverrides: { root: telaDeAcesso } },
    // O conteúdo não pode alargar a página: tabelas largas rolam dentro do próprio contêiner.
    RaLayout: {
      styleOverrides: {
        // O react-admin usa min-width: fit-content na raiz, o que faz a página inteira crescer com a tabela mais larga.
        root: { minWidth: 0, width: "100%", "& .RaLayout-content": { minWidth: 0 } },
      },
    },
    // No celular o botão "Criar" vira botão flutuante: a barra de ações fica vazia e não deve ocupar espaço.
    RaList: {
      styleOverrides: {
        root: {
          // A área da lista não pode alargar a página; tabelas largas rolam dentro do próprio quadro.
          "& .RaList-main": { minWidth: 0 },
          "& .RaDatagrid-tableWrapper": { overflowX: "auto" },
          "@media (max-width: 599.95px)": {
            "& .RaList-actions": { backgroundColor: "transparent", minHeight: 0 },
            "& .RaList-actions .RaTopToolbar-root": { minHeight: 0, padding: 0 },
          },
        },
      },
    },
    // Fichas e cadastros: o quadro também não pode crescer com uma tabela larga (ex.: precificação no celular).
    RaEdit: { styleOverrides: { root: { "& .RaEdit-main, & .RaEdit-card": { minWidth: 0 } } } },
    RaCreate: { styleOverrides: { root: { "& .RaCreate-main, & .RaCreate-card": { minWidth: 0 } } } },
    RaShow: { styleOverrides: { root: { "& .RaShow-main, & .RaShow-card": { minWidth: 0 } } } },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          "&.RaMenuItemLink-active": {
            borderLeft: `3px solid ${tokens.ciano}`,
            fontWeight: 700,
            color: tokens.navy,
          },
        },
      },
    },
  },
};

// Mescla rasa com o tema padrão do react-admin; os componentes são mesclados um nível abaixo.
// A conversão de tipo é necessária só porque `exactOptionalPropertyTypes` não aceita os opcionais do MUI.
export const tema = {
  ...defaultTheme,
  ...temaSobral,
  components: { ...defaultTheme.components, ...temaSobral.components },
} as RaThemeOptions;
